import {
  GoogleMap,
  InfoWindow,
  LoadScript,
  Marker,
} from '@react-google-maps/api';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { styled } from 'linaria/react';
import { LocationStateContext } from './StoreLocator';
import { useMapState } from './useMapState';
import { useShopConfig } from '@jetshop/core/hooks/useShopConfig';
import throwErrorInDev from '@jetshop/core/helpers/throwErrorInDev';

import { theme } from '../Theme';
import { StoreContext } from '../StoreHandler/StoreContext';

// Import the PNG files
import InactiveMarkerIcon from '../../svg/NewMapPin.png';
import ActiveMarkerIcon from '../../svg/NewMapPinActive.png';

const Wrapper = styled('div')`
  ${theme.above.md} {
    flex: 1;
  }

  ${theme.below.md} {
    padding: 0 12px;
  }

  .map-container {
    min-height: 100%;

    .gm-style-iw {
      background-color: white !important;
    }

    .gm-style-iw-a {
      width: 300px !important;
    }

    ${theme.below.md} {
      height: 50vh;
    }
    .gm-style-moc {
      display: none;
    }
  }
`;

const LoadingElement = () => <div style={{ height: '100%' }}></div>;

const StoreMap = ({
  stores,
  userLocation,
  closestStore,
  activeStore,
  long,
  lat,
}) => {
  const ref = useRef();
  const locationState = useContext(LocationStateContext);
  const { isOpen, infoId, showInfo } = useMapState();
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const { storeList } = useContext(StoreContext);

  // Filter out store ID 129 from storeList
  const filteredStoreList = storeList?.filter(
    (store) => !(store.id === 129 || store?.extra?.id === 129)
  );

  const { googleMapsApiKey } = useShopConfig();

  throwErrorInDev(
    typeof googleMapsApiKey === 'undefined',
    'Make sure googleMapsApiKey is defined in your shop.config.js. See https://developers.google.com/maps/documentation/javascript/get-api-key'
  );

  // useless comment

  useEffect(() => {
    if (filteredStoreList && mapsLoaded) {
      // Stores are loaded, fit bounds
      const bounds = new window.google.maps.LatLngBounds();

      // Filter out stores with invalid coordinates
      const validStores = filteredStoreList.filter(
        (store) =>
          store?.extra?.coordinates?.latitude &&
          store?.extra?.coordinates?.longitude &&
          !isNaN(store.extra.coordinates.latitude) &&
          !isNaN(store.extra.coordinates.longitude)
      );

      if (validStores.length === 0) return; // No valid stores, don't set bounds

      validStores.forEach((store) =>
        bounds.extend(
          new window.google.maps.LatLng(
            store.extra.coordinates.latitude,
            store.extra.coordinates.longitude
          )
        )
      );

      // Don't zoom in too far on only one marker
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      if (ne && sw && ne.equals(sw)) {
        const extendPoint1 = new window.google.maps.LatLng(
          ne.lat() + 0.01,
          ne.lng() + 0.01
        );
        const extendPoint2 = new window.google.maps.LatLng(
          ne.lat() - 0.01,
          ne.lng() - 0.01
        );
        bounds.extend(extendPoint1);
        bounds.extend(extendPoint2);
      }

      ref.current.fitBounds(bounds);
    } else {
      console.warn('Stores or map not loaded');
    }

    if (mapsLoaded && filteredStoreList.length === 0) {
      console.warn('No stores loaded, check if API is down.');
    }
  }, [mapsLoaded, filteredStoreList]);

  useEffect(() => {
    if (closestStore && mapsLoaded) {
      // User has shared its location and the closest store has been calculated
      const bounds = new window.google.maps.LatLngBounds();

      bounds.extend(
        new window.google.maps.LatLng(
          userLocation.latitude,
          userLocation.longitude
        )
      );
      bounds.extend(
        new window.google.maps.LatLng(
          closestStore.coordinates.latitude,
          closestStore.coordinates.longitude
        )
      );

      ref.current.fitBounds(bounds);
    }
  }, [userLocation, closestStore, mapsLoaded]);

  return (
    <Wrapper className="storemap-wrapper">
      <>
        <LoadScript
          loadingElement={<LoadingElement />}
          preventGoogleFontsLoading={true}
          googleMapsApiKey={googleMapsApiKey}
        >
          <GoogleMap
            defaultZoom={6}
            mapContainerClassName="map-container"
            defaultCenter={{ lat: 59.247948, lng: 14.755806 }}
            onLoad={(map) => {
              ref.current = map;
              setMapsLoaded(true);
            }}
          >
            <>
              {userLocation &&
                userLocation.latitude &&
                userLocation.longitude && (
                  <Marker
                    position={{
                      lat: userLocation.latitude,
                      lng: userLocation.longitude,
                    }}
                  />
                )}

              {mapsLoaded &&
                filteredStoreList
                  .filter(
                    (store) =>
                      store?.extra?.coordinates?.latitude &&
                      store?.extra?.coordinates?.longitude &&
                      !isNaN(store.extra.coordinates.latitude) &&
                      !isNaN(store.extra.coordinates.longitude)
                  )
                  .map((store, index) => {
                    if (store?.extra === undefined) return null;
                    const isActive =
                      activeStore ??
                      store?.extra.id === locationState.activeLocation;

                    return (
                      <React.Fragment key={store?.extra.id}>
                        <Marker
                          cursor={'pointer'}
                          clickable={true}
                          icon={{
                            url: isActive
                              ? ActiveMarkerIcon
                              : InactiveMarkerIcon,
                            scaledSize: new window.google.maps.Size(24, 29),
                            anchor: new window.google.maps.Point(12, 29),
                          }}
                          zIndex={isActive ? 1000 : index}
                          position={{
                            lat: lat ? lat : store.extra.coordinates.latitude,
                            lng: long
                              ? long
                              : store.extra.coordinates.longitude,
                          }}
                          onMouseOver={() => {
                            locationState.setHighlightedLocation(
                              store?.extra.id
                            );
                          }}
                          onMouseOut={() =>
                            locationState.setHighlightedLocation(null)
                          }
                          onClick={() => showInfo(store?.extra.id)}
                        />
                        {isOpen && infoId === store?.extra.id && (
                          <InfoWindow
                            key={`infowindow-${store?.extra.id}`}
                            onCloseClick={showInfo}
                            position={{
                              lat: store?.extra.coordinates.latitude,
                              lng: store?.extra.coordinates.longitude,
                            }}
                            options={{
                              pixelOffset: new window.google.maps.Size(50, 0),
                            }}
                          >
                            <div
                              style={{
                                background: 'white',
                                padding: '1em',
                                minWidth: '200px',
                                fontSize: '14px',
                                lineHeight: 1.5,
                              }}
                            >
                              <p>
                                <strong
                                  dangerouslySetInnerHTML={{
                                    __html: store.name,
                                  }}
                                />
                              </p>
                              {store?.storeInfo && store?.storeInfo?.address ? (
                                <div>
                                  <p>{store?.storeInfo?.address}</p>
                                  <p>
                                    {store?.storeInfo?.zip}{' '}
                                    {store?.storeInfo?.city}
                                  </p>
                                  <p>Telefon: {store?.storeInfo?.phone}</p>
                                </div>
                              ) : (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: store?.extra?.address1,
                                  }}
                                />
                              )}
                            </div>
                          </InfoWindow>
                        )}
                      </React.Fragment>
                    );
                  })}
            </>
          </GoogleMap>
        </LoadScript>
      </>
    </Wrapper>
  );
};

export default StoreMap;
